/**
 * ProductMain - Product Detail Page (PDP) Web Component
 *
 * Handles color swatch selection for products with alternate color options.
 * When a user selects a different color swatch, the component loads that product's
 * data and updates the entire PDP (images, description, price, variants, etc.)
 * without a full page reload.
 *
 * @requires product-json - Script JSON with current product (id, variants, metafields, etc.)
 * @requires product-colors-json - Optional. Array of alternate color products with metafields.
 *
 * Data attributes used:
 *   [data-main-product-title]      - Product title
 *   [data-main-product-vendor]    - Product vendor
 *   [data-main-product-handle]    - Product handle
 *   [data-main-product-url]        - Product URL
 *   [data-main-product-description] - Product description (HTML)
 *   [data-main-product-shipping-origin] - Shipping origin block
 *   [data-main-product-size-guide] - Size guide link container
 *   [data-main-product-size-guide-popup] - Size guide popup content
 *
 * Integrates with: product.js (collapsible panels, toggle-shipping), wishlist-ajax-product.js,
 * registry-ajax-product.js, product-galleries.js, Bold product options.
 */
class ProductMain extends HTMLElement {
  constructor() {
    super();
    this._colorsData = [];
    this._productJson = null;
    this._activeProduct = null;
    this._activeVariant = null;
    this._currentProductId = null;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  connectedCallback() {
    this._loadColors();
    this._loadProductJson();
    this._cacheProductElements();
    this._bindSwatchClicks();
    this._bindOptionChanges();
    this._bindQuantityChange();
    this._updateProductDataAttributes(this._activeProduct || this._productJson);
    this._updateVariant();
  }

  // ---------------------------------------------------------------------------
  // Data loading & caching
  // ---------------------------------------------------------------------------

  /** Cache references to product elements that update on swatch change */
  _cacheProductElements() {
    this._productEls = {
      title: [...this.querySelectorAll('[data-product-main-title]')],
      vendor: [...this.querySelectorAll('[data-product-main-vendor]')],
      handle: [...this.querySelectorAll('[data-product-main-handle]')],
      url: [...this.querySelectorAll('[data-product-main-url]')]
    };
  }

  /**
   * Parse product-colors-json and normalize to flat product objects.
   * Merges metafields into each product for kinful.size_guide, shipping_origin, etc.
   */
  _loadColors() {
    const jsonEl = document.getElementById('product-colors-json');
    if (!jsonEl) return;

    try {
      const raw = JSON.parse(jsonEl.textContent.trim());
      this._colorsData = raw.map((item) => {
        if (item.product && 'metafields' in item) {
          return {
            ...item.product,
            badge: item.badge || null,
            metafields: item.metafields,
            images_count: item.images_count ?? item.product?.images?.length ?? 0,
            featured_image_url: item.featured_image_url || null
          };
        }
        return item;
      });
      this._currentProductId = this._getCurrentProductId();
    } catch (e) {
      console.warn('product-main: could not parse product-colors-json', e);
    }
  }

  /** Load product-json (current product). Always present on PDP. */
  _loadProductJson() {
    const jsonEl = document.getElementById('product-json');
    if (!jsonEl) return;

    try {
      this._productJson = JSON.parse(jsonEl.textContent.trim());
      if (!this._activeProduct) this._activeProduct = this._productJson;
    } catch (e) {
      console.warn('product-main: could not parse product-json', e);
    }
  }

  _getCurrentProductId() {
    const page = this.querySelector('.product-page');
    return page?.dataset.productId ?? null;
  }

  // ---------------------------------------------------------------------------
  // Event bindings
  // ---------------------------------------------------------------------------

  /** Handle color swatch clicks - switch to alternate product */
  _bindSwatchClicks() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a.color-swatch[data-product-main-id], a.color-swatch[data-product-id]');
      if (!link) return;

      // Custom PDP with Bold: full page load so option sets stay product-specific
      if (this.querySelector('.product-details.custom .bold_options') && link.href) {
        const productId = link.dataset.productMainId ?? link.dataset.productId;
        if (String(this._currentProductId) === productId) {
          e.preventDefault();
        }
        return;
      }

      e.preventDefault();
      const productId = link.dataset.productMainId ?? link.dataset.productId;
      const productHandle = link.dataset.productHandle;
      if (String(this._currentProductId) === productId) return;

      const product = this._colorsData?.find(
        (p) => String(p.id) === productId || (productHandle && p.handle === productHandle)
      );
      if (product) {
        this._updateSwatchSelection(link);
        this._updateFromProduct(product);
        this.selectProduct(product);
      }
    });

  }

  /** Keep .registry-add data-quantity in sync with #Quantity input and +/- buttons */
  _bindQuantityChange() {
    const qtyInput = this.querySelector('#Quantity');
    const updateRegistryQuantity = () => {
      const val = qtyInput?.value ?? '1';
      this.querySelectorAll('.registry-add').forEach((el) => el.setAttribute('data-quantity', val));
    };
    if (qtyInput) {
      qtyInput.addEventListener('input', updateRegistryQuantity);
      qtyInput.addEventListener('change', updateRegistryQuantity);
    }
    this.addEventListener('click', (e) => {
      if (e.target.closest?.('.button-plus, .button-minus')) {
        setTimeout(updateRegistryQuantity, 0);
      }
    });
  }

  /** Handle option select/radio changes - triggers variant update */
  _bindOptionChanges() {
    this.addEventListener('change', (e) => {
      const t = e.target;
      if (t.id === 'select-one' || t.id === 'select-two' || t.id === 'select-three') {
        this._updateVariant();
        this._updateOptionLegend(t);
      }
      if (t.matches?.('.option-color-1, .option-color-2, .option-color-3')) {
        this._updateVariant();
        this._updateOptionLegend(t);
      }
      if (t.closest?.('.bold_options')) this._updateVariant();
    });
  }

  /** Update the legend span when option select changes (e.g. color name) */
  _updateOptionLegend(target) {
    const container = target.closest('.color-swatches-container');
    if (!container) return;
    const legend = container.querySelector('legend span');
    const title = target.options?.[target.selectedIndex]?.text ?? target.dataset?.title;
    if (legend && title) legend.textContent = title.replace(/\s*\(Sold Out\)\s*$/, '').trim();
  }

  /** Update visual swatch selection state and legend */
  _updateSwatchSelection(link) {
    const container = link.closest('.color-swatches');
    if (!container) return;

    const containerParent = container.closest('.color-swatches-container');
    if (!containerParent) return;

    container.querySelectorAll('a.color-swatch').forEach((el) => el.classList.remove('is-selected'));
    link.classList.add('is-selected');

    const legend = containerParent.querySelector('legend');
    const colorName = link.dataset.colorName;
    if (legend && colorName) {
      let span = legend.querySelector('span');
      if (!span) {
        span = document.createElement('span');
        legend.appendChild(span);
      }
      span.textContent = colorName;
    }

    this._currentProductId = link.dataset.productMainId ?? link.dataset.productId;
  }

  // ---------------------------------------------------------------------------
  // Variant matching
  // ---------------------------------------------------------------------------

  /**
   * Get current option values from selects, radios, or Bold option selects.
   * Values are decoded for comparison (handles + for space in URL encoding).
   */
  _getOptionValues() {
    const opt = { 1: '', 2: '', 3: '' };
    const decode = (s) => {
      if (!s) return '';
      try {
        return decodeURIComponent(String(s).replace(/\+/g, ' '));
      } catch {
        return String(s).replace(/\+/g, ' ');
      }
    };
    const product = this._activeProduct || this._productJson;
    const optionCount = Array.isArray(product?.options) ? product.options.length : 3;
    const boldSelects = [...this.querySelectorAll('.bold_options .bold_option_element select')];
    let boldIdx = 0;

    for (let i = 1; i <= 3; i++) {
      const sel = this.querySelector(`#select-${i === 1 ? 'one' : i === 2 ? 'two' : 'three'}`);
      const radios = this.querySelectorAll(`.option-color-${i}:checked`);
      const isShopifyOption = i <= optionCount;
      if (sel?.value) opt[i] = decode(sel.value);
      else if (radios.length && radios[0].value) opt[i] = decode(radios[0].value);
      else if (isShopifyOption && boldSelects[boldIdx]?.value) opt[i] = decode(boldSelects[boldIdx++].value);
      else if (isShopifyOption && product?.variants?.length && !sel && !radios.length) {
        const v = product.variants[0];
        opt[i] = (i === 1 ? v.option1 : i === 2 ? v.option2 : v.option3) || '';
      }
    }
    return opt;
  }

  /**
   * Match variant by current option values.
   * Falls back to product-select value, then first variant.
   * @returns {{ id, image_src, priceHtml, available } | null}
   */
  _matchVariant(product) {
    const variants = product?.variants || [];
    if (!variants.length) return null;

    const toResult = (v) => {
      const imageSrc = v.image_src || v.image?.src || product?.featured_image?.src || product?.featured_image?.url || '';
      let priceHtml = this._formatMoney(v.price, v.price_formatted);
      if (v.compare_at_price && v.compare_at_price !== v.price) {
        const compare = this._formatMoney(v.compare_at_price, v.compare_at_price_formatted);
        const savings = Math.abs(Math.round(((v.price - v.compare_at_price) / v.compare_at_price) * 100)) + '%';
        priceHtml = `<strike>${compare}</strike> ${priceHtml} <span class="savings">Save ${savings}</span>`;
      }
      return {
        id: v.id,
        image_src: imageSrc,
        priceHtml,
        available: v.available,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3
      };
    };

    if (variants.length === 1) return toResult(variants[0]);

    const opt = this._getOptionValues();
    const exactMatch = (v) =>
      (v.option1 || '') === (opt[1] || '') &&
      (v.option2 || '') === (opt[2] || '') &&
      (v.option3 || '') === (opt[3] || '');
    let v = variants.find(exactMatch);

    if (!v) {
      const partialMatch = (variant) => {
        if (opt[1] && (variant.option1 || '') !== opt[1]) return false;
        if (opt[2] && (variant.option2 || '') !== opt[2]) return false;
        if (opt[3] && (variant.option3 || '') !== opt[3]) return false;
        return true;
      };
      v = variants.find(partialMatch);
    }

    if (!v) {
      const productSelectEl = document.getElementById('product-select') || this.querySelector('#product-select');
      const currentId = productSelectEl?.value ? String(productSelectEl.value) : null;
      if (currentId) v = variants.find((x) => String(x.id) === currentId);
    }

    return v ? toResult(v) : null;
  }

  _formatMoney(cents, formatted) {
    const normalize = (value) => {
      if (typeof value !== 'string' || !value.trim()) return '';
      return value.replace(/\d[\d,]*(?:\.\d+)?/g, (numStr) => {
        const raw = numStr.replace(/,/g, '');
        const amount = Number(raw);
        if (Number.isNaN(amount)) return numStr;
        const hasCents = Math.round(amount * 100) % 100 !== 0;
        return amount.toLocaleString(undefined, {
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: hasCents ? 2 : 0
        });
      });
    };

    const normalizedFormatted = normalize(formatted);
    if (normalizedFormatted) return normalizedFormatted;

    if (typeof window.Shopify?.formatMoney === 'function') {
      return normalize(window.Shopify.formatMoney(cents));
    }

    const amount = Number(cents) / 100;
    const hasCents = Math.round(amount * 100) % 100 !== 0;
    return '$' + amount.toLocaleString(undefined, {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0
    });
  }

  // ---------------------------------------------------------------------------
  // Variant & form updates
  // ---------------------------------------------------------------------------

  /**
   * Refresh "(Sold Out)" labels on #select-one/two/three option text.
   * Mirrors Liquid's product_option_value.available semantics: a value is
   * available when at least one in-stock variant carries it and matches the
   * currently selected values of the preceding options. Runs on every option
   * change and swatch switch so labels never go stale.
   */
  _updateSoldOutLabels(product) {
    const variants = product?.variants || [];
    if (!variants.length) return;

    const decode = (s) => {
      if (!s) return '';
      try {
        return decodeURIComponent(String(s).replace(/\+/g, ' '));
      } catch {
        return String(s).replace(/\+/g, ' ');
      }
    };
    const opt = this._getOptionValues();

    ['one', 'two', 'three'].forEach((word, idx) => {
      const sel = this.querySelector(`#select-${word}`);
      if (!sel) return;
      const n = idx + 1;
      [...sel.options].forEach((option) => {
        const value = decode(option.value);
        const inStock = variants.some((v) => {
          if ((v[`option${n}`] || '') !== value) return false;
          for (let p = 1; p < n; p++) {
            if (opt[p] && (v[`option${p}`] || '') !== opt[p]) return false;
          }
          return !!v.available;
        });
        const base = option.text.replace(/\s*\(Sold Out\)\s*$/, '').trim();
        option.text = inStock ? base : `${base} (Sold Out)`;
      });
    });
  }

  /**
   * Update PDP for current variant: price, add-to-cart, registry/wishlist data,
   * main image, URL, and Bold integration.
   */
  _updateVariant() {
    const product = this._activeProduct || this._productJson;
    if (!product) return;

    const handle = product.handle || '';
    const productId = product.id;
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    const queryString = url.searchParams.toString();

    this._updateSoldOutLabels(product);

    const variant = this._matchVariant(product);
    const form = document.getElementById('AddToCartForm') || this.querySelector('form[id="AddToCartForm"]') || this.querySelector('form');
    const priceEl = (form && form.querySelector('#price')) || this.querySelector('#price');
    const productSelect = (form && (form.querySelector('#product-select') || form.querySelector('[name="id"]'))) || document.getElementById('product-select') || (form && form.querySelector('[name="id"]'));
    const addBtn = (form && form.querySelector('#AddToCart')) || this.querySelector('#AddToCart');
    const bisBtns = form
      ? form.querySelectorAll('.product-back-in-stock[data-bis-open-trigger]')
      : this.querySelectorAll('.product-back-in-stock[data-bis-open-trigger]');
    const registryLinks = this.querySelectorAll('.registry-link');
    const productPage = this.querySelector('.product-page');
    const mainImages = this.querySelectorAll('.product-details__main-image');

    if (!variant) {
      if (productSelect) productSelect.value = '';
      if (priceEl) priceEl.innerHTML = 'Unavailable';
      registryLinks.forEach((el) => el.removeAttribute('data-variant-id'));
      if (productPage) productPage.dataset.productVariantId = '';
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
      if (bisBtns.length) {
        bisBtns.forEach((btn) => btn.classList.add('hidden'));
      }
      history.replaceState(null, null, `/products/${handle}${queryString ? '?' + queryString : ''}`);
      return;
    }

    this._activeVariant = variant;
    window.Shopify.product = product;
    window.Shopify.variant = variant;

    if (priceEl) priceEl.innerHTML = variant.priceHtml;
    if (productPage) productPage.dataset.productVariantId = String(variant.id);
    registryLinks.forEach((el) => el.setAttribute('data-variant-id', String(variant.id)));

    const isUnavailable = !variant.available || (productSelect && productSelect.querySelector(`option[value="${variant.id}"]`)?.disabled);
    if (addBtn) {
      addBtn.disabled = !!isUnavailable;
      addBtn.textContent = isUnavailable ? 'Sold Out' : 'Add To Shopping Bag';
    }
    // When BIS is enabled, hide add-to-cart while sold out so the notify CTA is the focus.
    // If `<klaviyo-back-in-stock>` / BIS triggers are not in the theme, keep add buttons visible
    // (e.g. disabled "Sold Out") instead of display:none with no replacement.
    const hasBisProductTrigger = bisBtns.length > 0;
    this.querySelectorAll('[name="add"]').forEach((el) => {
      if (!hasBisProductTrigger) {
        el.style.removeProperty('display');
        return;
      }
      el.style.display = isUnavailable ? 'none' : 'block';
    });

    if (bisBtns.length) {
      bisBtns.forEach((bisBtn) => {
        bisBtn.toggleAttribute('hidden', !isUnavailable);
        bisBtn.classList.toggle('hidden', !isUnavailable);
        bisBtn.style.display = isUnavailable ? 'block' : 'none';
        // Keep BIS trigger dataset in sync even on initial load; the modal
        // reads these values when opened.
        bisBtn.dataset.bisVariantId = String(variant.id);
        bisBtn.dataset.bisTitleText = product.title || bisBtn.dataset.bisTitleText;

        const opts = Array.isArray(product.options) ? product.options : [];
        const vals = [variant.option1, variant.option2, variant.option3];
        for (let i = 0; i < 3; i += 1) {
          const line = i + 1;
          const label = opts[i] ? `${opts[i]}: ` : '';
          const value = vals[i] || '';
          bisBtn.dataset[`bisLine${line}Label`] = label;
          bisBtn.dataset[`bisLine${line}Value`] = value;
        }
      });
    }

    const variantIdStr = String(variant.id);
    if (productSelect) {
      if (productSelect.tagName === 'SELECT') {
        const hasOption = productSelect.querySelector(`option[value="${variantIdStr}"]`);
        if (!hasOption) {
          const opt = document.createElement('option');
          opt.value = variantIdStr;
          opt.textContent = variantIdStr;
          productSelect.appendChild(opt);
        }
      }
      productSelect.value = variantIdStr;
      productSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (form) {
      form.querySelectorAll('[name="id"]').forEach((el) => {
        if (el !== productSelect && el.value !== variantIdStr) {
          el.value = variantIdStr;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    if (typeof window.BOLD === 'object' && window.BOLD.options?.app?.emit) {
      try { window.BOLD.options.app.emit('variant_changed', { variant: { id: variant.id } }); } catch (_) {}
    }

    history.replaceState(null, null, `/products/${handle}?variant=${variant.id}${queryString ? '&' + queryString : ''}`);

    if (mainImages.length) {
      mainImages.forEach((el) => el.classList.add('hidden'));
      const match = this.querySelector(`.product-details__main-image[data-featured-image-src="${variant.image_src}"]`);
      if (match) match.classList.remove('hidden');
      else if (mainImages[0]) mainImages[0].classList.remove('hidden');
    }

    const customerId = document.body.dataset?.customerId;
    if (typeof window.readCookie === 'function' && window.readCookie('registry_slug')) {
      this._checkRegistryProduct(customerId, productId, variant.id);
    } else {
      this.querySelectorAll('.registry-add, .registry-remove').forEach((el) => (el.style.display = 'none'));
    }

    // Notify personalization (and other listeners) of variant-driven product data changes.
    const preservePersonalization = this._personalizationResetNext !== true;
    this._personalizationResetNext = false;
    this.dispatchEvent(
      new CustomEvent('personalization:product-change', {
        bubbles: true,
        detail: {
          product,
          variant,
          preservePersonalization,
        },
      })
    );
  }

  /** Check if product is in customer's registry; show add/remove accordingly */
  _checkRegistryProduct(shopifyCustomerId, shopifyProductId, shopifyVariantId) {
    const slug = typeof window.readCookie === 'function' ? window.readCookie('registry_slug') : '';
    if (!slug || !shopifyCustomerId) {
      this.querySelectorAll('.registry-add, .registry-remove').forEach((el) => (el.style.display = 'none'));
      return;
    }
    const url = `/apps/registry/${slug}/products/search?registry_id=${encodeURIComponent(slug)}&customer[shopify_customer_id]=${encodeURIComponent(shopifyCustomerId)}&product[shopify_product_id]=${encodeURIComponent(shopifyProductId)}&product[shopify_variant_id]=${encodeURIComponent(shopifyVariantId)}`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const add = this.querySelector('.registry-add');
        const remove = this.querySelector('.registry-remove');
        if (!data.success) {
          if (add) add.style.display = 'none';
          if (remove) remove.style.display = 'none';
          return;
        }
        if (data.product_found) {
          if (add) add.style.display = 'none';
          if (remove) {
            remove.dataset.registryProductId = data.product?.id ?? '';
            remove.style.display = 'block';
          }
        } else {
          if (add) add.style.display = 'block';
          if (remove) remove.style.display = 'none';
        }
      })
      .catch(() => {
        this.querySelectorAll('.registry-add, .registry-remove').forEach((el) => (el.style.display = 'none'));
      });
  }

  // ---------------------------------------------------------------------------
  // Product data updates (on swatch change)
  // ---------------------------------------------------------------------------

  /** Update title, vendor, handle, URL, and all data-attribute-driven content */
  _updateFromProduct(product) {
    if (!this._productEls) return;
    const v = (x) => (x != null ? String(x) : '');
    this._productEls.title.forEach((el) => { el.textContent = v(product.title); });
    this._productEls.vendor.forEach((el) => { el.textContent = v(product.vendor); });
    this._productEls.handle.forEach((el) => { el.textContent = v(product.handle); });
    this._productEls.url.forEach((el) => { el.setAttribute('href', v(product.url)); });
    this._updateProductDataAttributes(product);
  }

  /**
   * Update all [data-main-product-*] driven content: description, final-sale,
   * shipping origin, size guide. Called on init and swatch change.
   */
  _updateProductDataAttributes(product) {
    if (!product) return;
    const productPage = this.querySelector('.product-page');
    const descEl = this.querySelector('[data-main-product-description]');
    const hasFinalSale = product.tags && (
      Array.isArray(product.tags) ? product.tags.includes('final-sale') : String(product.tags).includes('final-sale')
    );

    const finalSaleEl = this.querySelector('.final-sale[data-main-product-final-sale]');
    if (finalSaleEl) {
      finalSaleEl.style.display = hasFinalSale ? '' : 'none';
      if (hasFinalSale) {
        finalSaleEl.innerHTML = 'Final Sale: <span class="final-sale-helper">This item is not eligible for return.</span>';
      }
    }

    if (descEl) {
      const desc = product.description || '';
      const plain = desc.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      descEl.dataset.mainProductDescription = plain;
      descEl.innerHTML = desc;
      this._initProductDescriptionPanels();
    }

    this._updateShippingOrigin(product);
    this._updateSizeGuide(product);
    this._updateImageLayout(product);
  }

  /**
   * Update image layout classes and visibility when product has alternate_color_options.
   * Toggles grid layout (multi vs single image) and shows/hides arrows + thumbnails.
   */
  _updateImageLayout(product) {
    const layoutEl = this.querySelector('[data-main-product-image-layout]');
    if (!layoutEl) return;

    const count = product?.images_count ?? product?.images?.length ?? 0;
    const hasMultiple = count > 1;

    const multi = layoutEl.dataset.layoutMulti || '';
    const single = layoutEl.dataset.layoutSingle || '';
    const base = 'grid__item one-whole text-center';
    layoutEl.className = `${base} ${hasMultiple ? multi : single}`;

    const arrowsEl = this.querySelector('[data-main-product-image-arrows]');
    if (arrowsEl) arrowsEl.style.display = hasMultiple ? '' : 'none';

    const thumbsEl = this.querySelector('[data-main-product-thumbs]');
    if (thumbsEl) thumbsEl.style.display = hasMultiple ? '' : 'none';
  }

  /**
   * Update size guide link and popup content.
   * Priority: kinful.size_guide (popup) > otm.show_size_guide_link (external).
   * Default PDP falls back to vendor help URL; custom PDP stays empty.
   */
  _updateSizeGuide(product) {
    const linkEl = this.querySelector('[data-main-product-size-guide]');
    const popupEl = this.querySelector('[data-main-product-size-guide-popup]');
    if (!linkEl) return;

    const sg = product?.metafields?.kinful?.size_guide;
    const otm = product?.metafields?.otm;
    const showOtmLink = otm?.show_size_guide_link === 'true' || otm?.show_size_guide_link === true;
    const otmLink = otm?.size_guide_link || '';
    const isCustom = !!this.querySelector('.product-details.custom');

    const hasSizeGuideData = sg && (sg.column_1 || sg.column_2 || sg.name || sg.image_src || sg.image);
    if (hasSizeGuideData) {
      linkEl.innerHTML = '<a href="#" onclick="document.getElementById(\'size-guide-popup\').classList.add(\'active\'); return false;">Size Guide</a>';
    } else if (showOtmLink && otmLink) {
      linkEl.innerHTML = `<a href="${String(otmLink).replace(/"/g, '&quot;')}" class="help-popout">Size Guide</a>`;
    } else if (isCustom) {
      linkEl.innerHTML = '';
    } else {
      const vendor = (product?.vendor || '').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
      linkEl.innerHTML = `<a href="https://help.overthemoon.com/${vendor}" class="help-popout">Size Guide</a>`;
    }

    if (popupEl && sg) {
      popupEl.innerHTML = this._buildSizeGuidePopupContent(sg);
    } else if (popupEl) {
      popupEl.innerHTML = '';
    }
  }

  /**
   * Build HTML for size guide popup from metafield data.
   * Supports column_1..column_10 (arrays or { value: [] }), name, description, image.
   */
  _buildSizeGuidePopupContent(sg) {
    const col = (c) => (Array.isArray(c) ? c : (c?.value ?? []));
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const cols = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      .map((i) => col(sg[`column_${i}`]))
      .filter((c) => c.length > 0);
    const columnCount = cols.length;
    const rowCount = cols[0] ? cols[0].length - 1 : 0;

    let html = '<div class="size-guide-popup--content__text"><div>';
    if (sg.show_name && sg.name) html += `<h1>${esc(sg.name)}</h1>`;
    if (sg.description) html += `<div class="rte">${sg.description}</div>`;
    html += '</div>';

    if (cols.length > 0 && rowCount > 0) {
      const rowStyle = sg.table_max_width ? ` style="width:${esc(sg.table_max_width)}px;min-width:${esc(sg.table_max_width)}px;overflow:auto;"` : '';
      html += `<div class="size-guide-popup-table"><div class="size-guide-table-head size-guide-table-row${columnCount > 1 ? ` size-guide-table-row--${columnCount}` : ''}"${rowStyle}>`;
      cols.forEach((c) => { html += `<div class="size-guide-table--header"><span>${esc(c[0])}</span></div>`; });
      html += '</div>';
      for (let i = 1; i <= rowCount; i++) {
        html += `<div class="size-guide-table-row size-guide-table-row--${columnCount}"${rowStyle}>`;
        cols.forEach((c) => { html += `<div><span>${esc(c[i])}</span></div>`; });
        html += '</div>';
      }
      html += '</div>';
    }
    html += '</div>';

    const imgSrc = sg.image_src || (sg.image && typeof sg.image === 'string' && sg.image.startsWith('http') ? sg.image : null);
    const showImageOnly = cols.length === 0 && imgSrc;
    html += '<div class="size-guide-popup--content">';
    if (showImageOnly) html += `<img class="size-guide-popup--content__image" src="${esc(imgSrc)}" loading="lazy" alt="">`;
    html += '</div>';
    return html;
  }

  /** Update shipping origin block from kinful.shipping_origin metafield */
  _updateShippingOrigin(product) {
    const el = this.querySelector('[data-main-product-shipping-origin]');
    if (!el) return;
    const so = product?.metafields?.kinful?.shipping_origin;
    if (!so?.country) {
      el.innerHTML = '';
      return;
    }
    const country = String(so.country).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const description = so.description || '';
    const flag = so.flag || '';
    const flagUrl = flag && flag.startsWith('//') ? 'https:' + flag : flag;
    const imgHtml = flagUrl ? `<img class="" loading="lazy" style="display:inline-block;width:30px;vertical-align:middle;" src="${flagUrl.replace(/"/g, '&quot;')}" alt="">` : '';
    el.innerHTML = `<div class="shipping-origin"><button type="button" class="toggle-shipping inner" aria-expanded="false" aria-controls="shipping-origin-content" id="shipping-origin-trigger">${imgHtml}<span>Shipping Origin: ${country}</span><span class="small">How it Works</span></button><div id="shipping-origin-content" class="content" role="region" aria-labelledby="shipping-origin-trigger">${description}</div></div>`;
  }

  /**
   * Initialize collapsible panels in product description.
   * Converts h3 triggers to buttons for a11y, adds aria attributes.
   * Mirrors product.js logic; required after description innerHTML is replaced.
   * Requires jQuery.
   */
  _initProductDescriptionPanels() {
    const descEl = this.querySelector('[data-main-product-description]');
    if (!descEl || typeof $ === 'undefined') return;
    const $desc = $(descEl);

    $desc.find('p').each(function() {
      const $p = $(this);
      if ($p.html() === '&nbsp;') $p.remove();
    });

    const $panelTriggers = $desc.find('h3');
    if (!$panelTriggers.length) return;

    $panelTriggers.each(function(i) {
      const $h3 = $(this);
      if ($h3.nextUntil('h3, h4').length) {
        $h3.nextUntil('h3, h4').wrapAll('<div class="product-info-panel"></div>');
      }
    });

    const $triggers = $desc.find('h3');
    $triggers.each(function(i) {
      const $h3 = $(this);
      const text = $h3.text().trim();
      const panelId = `product-info-panel-${i}`;
      const triggerId = `product-info-trigger-${i}`;
      const $panel = $h3.next('.product-info-panel');
      const isFirst = i === 0;
      const isExpanded = isFirst;

      const $btn = $('<button type="button" class="product-info-panel-trigger" id="' + triggerId + '" aria-expanded="' + isExpanded + '" aria-controls="' + panelId + '"></button>').text(text);
      if (isFirst) $btn.addClass('product-info-panel-trigger--first active');

      $h3.replaceWith($btn);

      if ($panel.length) {
        $panel.attr('id', panelId);
        $panel.attr('role', 'region');
        $panel.attr('aria-labelledby', triggerId);
        if (!isExpanded) $panel.hide();
      }
    });

    $desc.find('.product-info-panel-trigger').off('click').on('click', function(e) {
      e.preventDefault();
      const $trigger = $(this);
      const $infoPanel = $trigger.next('.product-info-panel');
      const isExpanded = $trigger.attr('aria-expanded') === 'true';
      $trigger.toggleClass('active');
      $trigger.attr('aria-expanded', !isExpanded);
      $infoPanel.slideToggle();
    });
  }

  // ---------------------------------------------------------------------------
  // Gallery & media
  // ---------------------------------------------------------------------------

  /** Replace main image, thumbnails, and gallery slides with new product media */
  _updateGallery(product) {
    const media = product.media || product.images || [];
    if (!media.length) return;

    const src = (m) => (m.src || m.url || '').replace(/^\//, 'https://');
    const featuredSrc = product.featured_image_url || src(media[0]);

    const mainWrapper = this.querySelector('.product-details__main-image-wrapper');
    const thumbInner = this.querySelector('.product-details__thumb-scroller-inner');
    const gallerySlides = this.querySelector('#otm-gallery .js-otm-gallery-slides');
    const galleryTitle = this.querySelector('.otm-gallery__product-title');
    const pdpBadge = this.querySelector('[data-product-main-badge]');

    if (mainWrapper) {
      mainWrapper.innerHTML = media
        .map((m, i) => {
          const cls = i === 0 ? '' : 'hidden';
          const imgSrc = i === 0 && product.featured_image_url ? featuredSrc : src(m);
          const priorityAttrs = i === 0 ? ' fetchpriority="high" loading="eager"' : '';
          if ((m.media_type || m.type) === 'video') {
            return `<div class="product-details__main-image js-product-image ${cls}" data-image-src="${src(m)}" data-featured-image-src="${featuredSrc}" data-image-index="${i}"><video src="${src(m)}" controls playsinline></video></div>`;
          }
          return `<img class="product-details__main-image js-product-image ${cls}" src="${imgSrc}" alt="${(m.alt || '').replace(/"/g, '&quot;')}" data-image-src="${src(m)}" data-featured-image-src="${featuredSrc}" data-image-index="${i}"${priorityAttrs}>`;
        })
        .join('');
    }

    if (thumbInner) {
      thumbInner.innerHTML = media
        .map((m, i) => {
          const imgSrc = src(m);
          const thumbSrc = imgSrc + (imgSrc.includes('?') ? '&' : '?') + 'width=300&height=300&fit=crop';
          const largeSrc = imgSrc + (imgSrc.includes('?') ? '&' : '?') + 'width=1000';
          if ((m.media_type || m.type) === 'video') {
            return `<a class="js-product-thumbnail video-thumbnail" href="${imgSrc}" data-large-url="${largeSrc}" data-image-src="${imgSrc}"><img src="${thumbSrc}" alt="${(m.alt || '').replace(/"/g, '&quot;')}"></a>`;
          }
          return `<a class="js-product-thumbnail" href="${imgSrc}" data-large-url="${largeSrc}" data-image-src="${imgSrc}"><img src="${thumbSrc}" alt="${(m.alt || '').replace(/"/g, '&quot;')}"></a>`;
        })
        .join('');
    }

    if (gallerySlides) {
      gallerySlides.innerHTML = media
        .map((m) => {
          if ((m.media_type || m.type) === 'video') {
            return `<div class="otm-gallery__slide"><video src="${src(m)}" controls playsinline></video></div>`;
          }
          const imgSrc = src(m) + (src(m).includes('?') ? '&' : '?') + 'width=1200';
          return `<div class="otm-gallery__slide"><img src="${imgSrc}" alt="${(m.alt || '').replace(/"/g, '&quot;')}"></div>`;
        })
        .join('');
    }

    if (galleryTitle) galleryTitle.textContent = product.title || '';

    if (pdpBadge) pdpBadge.innerHTML = product.badge || '';
  }

  // ---------------------------------------------------------------------------
  // Public hook
  // ---------------------------------------------------------------------------

  /**
   * Main entry point when switching products (e.g. color swatch).
   * Override to extend behavior. Updates gallery, form, variant, registry/wishlist IDs.
   * @param {Object} product - Full product object with variants, metafields, media
   */
  selectProduct(product) {
    this._activeProduct = product;
    window.Shopify.product = product;
    this._updateGallery(product);

    const productPage = this.querySelector('.product-page');
    if (productPage) productPage.setAttribute('data-product-id', product.id);

    this._deferProductRecommendationsRefresh(product.id);

    if (!product.variants?.length) return;

    const selectOne = this.querySelector('#select-one');
    if (selectOne) {
      const values = [...new Set(product.variants.map((v) => v.option1).filter(Boolean))];
      selectOne.innerHTML = values
        .map((val) => {
          const encoded = encodeURIComponent(val);
          return `<option value="${encoded}" dir="ltr">${val}</option>`;
        })
        .join('');
      selectOne.value = values[0] ? encodeURIComponent(values[0]) : '';
    }

    const productSelect = this.querySelector('#product-select');
    if (productSelect) {
      productSelect.innerHTML = product.variants
        .map(
          (v) =>
            `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${v.title ?? v.option1 ?? ''}</option>`
        )
        .join('');
      const firstAvailable = product.variants.find((v) => v.available);
      productSelect.value = firstAvailable ? firstAvailable.id : product.variants[0]?.id ?? '';
    }

    const registryAdd = this.querySelector('.registry-add');
    const registryRemove = this.querySelector('.registry-remove');
    if (registryAdd) registryAdd.setAttribute('data-product-id', product.id);
    if (registryRemove) registryRemove.setAttribute('data-product-id', product.id);

    this.querySelectorAll('.wishlist-add, .wishlist-remove').forEach((el) => {
      el.setAttribute('data-product-id', product.id);
    });

    // Alternate-color product switch: reset personalization on the following variant update.
    this._personalizationResetNext = true;

    this._updateVariant();
  }

  /**
   * Deferred refresh of product-recommendations when product changes.
   * Non-blocking; runs when browser is idle or on next tick.
   */
  _deferProductRecommendationsRefresh(productId) {
    const refresh = () => {
      const rec = this.querySelector('product-recommendations');
      if (rec && productId) {
        rec.setAttribute('product', productId);
        if (typeof rec.refresh === 'function') rec.refresh();
      }
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => refresh(), { timeout: 2000 });
    } else {
      setTimeout(refresh, 0);
    }
  }
}

customElements.define('product-main', ProductMain);
